/* ============================================================
   Dathent — Onboarding chat (Phase 3: real AI dialog)
   Real chat with the onboarding-worker AI interviewer.
   No scripted steps — AI drives each turn, returns the next
   question + 2-4 suggested-answer chips for one-tap responses.

   Backend integration:
     - api.onboarding.start()         on mount (resume or create)
     - api.onboarding.getStatus()     on mount (resume in-progress conversations)
     - api.onboarding.sendAiMessage(...) every user message
       returns: { assistantMessage, suggestedAnswers, extracted,
                  suggestedNextStep, canComplete, turnCount, maxTurns,
                  uiHints, provider, model, fallback }
     - api.onboarding.complete()      on user clicking "Enter workspace"
   ============================================================ */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../components/pack-icons';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { api } from '../services/api/client.js';
import { OnboardingActivation, researchOnboardingEnabled } from './OnboardingActivation.jsx';
import IntakeCard from './Onboarding/IntakeCard.jsx';
import { cleanSitePatch, websiteSourceMetadata } from '../lib/websiteIntake.js';
import OnboardingLangPicker from './Onboarding/OnboardingLangPicker.jsx';
import { intakeCardEnabled, freePreviewClientEnabled } from '../lib/flags.js';
import { markOnboardingSkipped } from '../lib/onboardingSkip.js';
import { consumeSitePrefill } from '../lib/onboardingSitePrefill.js';
import { rasterizeSvgToPng } from '../lib/rasterize.js';
import { track } from '../lib/funnel.js';
import { getFunnelSource } from '../lib/funnelSource.js';
import QuickBrandSheet from '../components/QuickBrandSheet.jsx';
import { useBrand } from '../contexts/BrandContext.jsx';
import { freePublishingState } from '../lib/freePublishing.js';
import { freePreviewState } from '../lib/freePreview.js';

/* Drip-in hook: shows messages one at a time with typing dots between. */
function useBotTurn(messages, onDone) {
  const [shown, setShown] = useState([]);
  const [typing, setTyping] = useState(false);
  const idx = useRef(0);
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  useEffect(() => {
    setShown([]); idx.current = 0;
    if (!messages || messages.length === 0) { setTyping(false); onDoneRef.current?.(); return; }
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const i = idx.current;
      if (i >= messages.length) { setTyping(false); onDoneRef.current?.(); return; }
      const len = messages[i].length;
      const delay = Math.min(1400, 300 + len * 8);
      setTyping(true);
      setTimeout(() => {
        if (cancelled) return;
        setShown((s) => [...s, messages[i]]);
        idx.current += 1;
        setTyping(false);
        setTimeout(tick, 280);
      }, delay);
    };
    tick();
    return () => { cancelled = true; };
  }, [messages]);

  return { shown, typing };
}

/* ---------------- Visual primitives ---------------- */
function BotAvatar({ size = 28 }) {
  return (
    <span className="ob-avatar" style={{ width: size, height: size }}>
      <img src="/logo.png" alt="Dathent" width={size} height={size}
           style={{ display: 'block', borderRadius: '50%' }} />
    </span>
  );
}

function TypingBubble() {
  return (
    <div className="ob-msg ob-msg-bot">
      <BotAvatar size={28} />
      <div className="ob-bubble ob-bubble-bot ob-typing">
        <span className="ob-tdot" /><span className="ob-tdot" /><span className="ob-tdot" />
      </div>
    </div>
  );
}

// Inline spinner shown inside the Send button while the AI is thinking.
// Pure SVG + CSS rotation — no external icon font needed.
function SendSpinner() {
  return (
    <svg className="ob-send-spinner" width="12" height="12" viewBox="0 0 24 24" fill="none"
         aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5"
              strokeOpacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" />
    </svg>
  );
}

// Inline language picker — single-select. Onboarding gets ONE default
// language; everything (text + image) is generated in it. After
// connecting accounts the user can override per-channel in Settings.
// Sending one click immediately so there's no extra "confirm" step
// (the picker IS the answer).
function LanguagePicker({ onPick, disabled }) {
  const { t } = useTranslation();
  return (
    <div className="ob-vs ob-langs">
      <span className="ob-vs-h">{t('onboardingChat.languagePickerHeading')}</span>
      <p className="ob-langs-help">
        {t('onboardingChat.languagePickerHelp')}
      </p>
      <div className="ob-langs-grid">
        {LANGUAGE_PRESETS.map((p) => (
          <button
            key={p.code}
            type="button"
            className="ob-lang-chip"
            disabled={disabled}
            onClick={() => onPick(t('onboardingChat.primaryLanguageAnswer', { name: p.name }), p.code)}
          >
            <span className="ob-lang-code">{p.code.toUpperCase()}</span>
            <span className="ob-lang-name">{p.name}</span>
            <span className="ob-lang-native">{p.native}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Inline visual-style picker — the primary input surface when the AI
// is on the visual_style step. Rendered ABOVE the text input so it
// reads as the canonical answer; the textarea below is the "describe
// your own" fallback. Each card is a styled swatch that on click
// sends the canonical preset key as the user's answer.
// Multi-select visual-style picker. The user can pick one OR several styles —
// the first selected is the primary (★, used for single posts + weighted in the
// batch rotation). Confirm sends one message listing all picked keys, which the
// backend parses deterministically into visualStyles[].
function VisualStylePicker({ onConfirm, disabled }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);
  const toggle = (key) => setSelected((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  return (
    <div className="ob-vs">
      <span className="ob-vs-h">{t('onboardingChat.visualStylePickerHeading')}</span>
      <div className="ob-vs-grid">
        {VISUAL_STYLE_PRESETS.map((p) => {
          const on = selected.includes(p.key);
          const isPrimary = on && selected[0] === p.key;
          return (
            <button key={p.key} type="button"
                    className={`ob-vs-card ${on ? 'is-active' : ''} ${isPrimary ? 'is-primary' : ''}`}
                    disabled={disabled}
                    onClick={() => toggle(p.key)}>
              <span className="ob-vs-swatch"
                    style={{ background: `linear-gradient(135deg, ${p.from} 0%, ${p.to} 100%)` }} />
              <span className="ob-vs-text">
                <span className="ob-vs-label">{isPrimary ? `★ ${p.label}` : p.label}</span>
                <span className="ob-vs-desc">{p.desc}</span>
              </span>
            </button>
          );
        })}
      </div>
      <button type="button" className="ob-vs-confirm"
              disabled={disabled || selected.length === 0}
              onClick={() => onConfirm(selected)}>
        {selected.length > 1
          ? t('onboardingChat.visualStyleConfirmMany', { count: selected.length })
          : t('onboardingChat.visualStyleConfirm')}
      </button>
    </div>
  );
}

// Website insight card — the "AI read your site" moment. Appears above the
// textbox after the user pastes a link anywhere in the chat. The analyzer
// runs in parallel with the normal AI turn (never blocks it); this card is
// the only UI it adds. Three states: loading pill → suggestion card
// (one primary CTA + quiet skip) → applied success pill.
function WebsiteInsightCard({ check, busy, onApply, onDismiss }) {
  const { t } = useTranslation();
  if (check.status === 'loading') {
    return (
      <div className="ob-site ob-site-pill">
        <SendSpinner /> <span>{t('onboardingChat.siteAnalyzing', { host: check.host })}</span>
      </div>
    );
  }
  if (check.status === 'applied') {
    return (
      <div className="ob-site ob-site-pill ob-site-done">
        <I.check size={13} /> <span>{t('onboardingChat.siteCardApplied')}</span>
      </div>
    );
  }
  const rows = sitePreviewRows(check.result?.patch, t);
  return (
    <div className="ob-site ob-site-card" role="region" aria-label={t('onboardingChat.siteCardTitle')}>
      <div className="ob-site-head">
        <span className="ob-site-ic" aria-hidden="true"><I.sparkles size={15} /></span>
        <span className="ob-site-titles">
          <span className="ob-site-title">{t('onboardingChat.siteCardTitle')}</span>
          <span className="ob-site-host"><I.globe size={11} /> {check.host}</span>
        </span>
      </div>
      <div className="ob-site-rows">
        {rows.map((row) => (
          <div key={row.key} className="ob-site-row">
            <span className="ob-site-k">{row.label}</span>
            {row.items ? (
              <span className="ob-site-pills">
                {row.items.map((item, index) => (
                  <span key={index} className="ob-site-tag">{item}</span>
                ))}
              </span>
            ) : (
              <span className="ob-site-v">{row.value}</span>
            )}
          </div>
        ))}
      </div>
      <div className="ob-site-actions">
        <button type="button" className="btn btn-primary btn-sm ob-site-apply" disabled={busy} onClick={onApply}>
          {busy ? <SendSpinner /> : <I.sparkles size={12} />}
          {busy ? t('onboardingChat.siteCardApplying') : t('onboardingChat.siteCardApply')}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={onDismiss}>
          {t('onboardingChat.siteCardSkip')}
        </button>
        <span className="ob-site-note">{t('onboardingChat.siteCardHint')}</span>
      </div>
    </div>
  );
}

function BotMessage({ text, withAvatar = true }) {
  return (
    <div className={`ob-msg ob-msg-bot${withAvatar ? '' : ' ob-msg-no-avatar'}`}>
      {withAvatar ? <BotAvatar size={28} /> : <span style={{ width: 28 }} />}
      <div className="ob-bubble ob-bubble-bot" style={{ whiteSpace: 'pre-wrap' }}>{text}</div>
    </div>
  );
}

function UserAnswer({ text }) {
  return (
    <div className="ob-msg ob-msg-user">
      <div className="ob-bubble ob-bubble-user">
        <span className="ob-user-summary" style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
      </div>
    </div>
  );
}

/* ---------------- Main component ---------------- */

function buildGreeting(firstName, t) {
  return [
    firstName
      ? t('onboardingChat.greetingFirstNamed', { name: firstName })
      : t('onboardingChat.greetingFirstAnon'),
    t('onboardingChat.greetingSecond'),
    t('onboardingChat.greetingThird'),
  ];
}

// ── Chat-draft persistence ──────────────────────────────────────────────
// The server already remembers extracted fields, but the visible chat
// (history + the current question + chips + progress) lives only in React
// state — so a refresh wiped the conversation. We mirror it to localStorage,
// scoped to the user id (a different account on this browser never inherits
// it) with a 24h TTL. Cleared when the chat hands off to activation.
const CHAT_DRAFT_KEY = 'dathent_onboarding_chat_state';
const CHAT_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

function readChatDraft(userId) {
  try {
    const raw = window.localStorage.getItem(CHAT_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.savedAt !== 'number' || !Array.isArray(parsed.history)) {
      window.localStorage.removeItem(CHAT_DRAFT_KEY);
      return null;
    }
    if (parsed.userId !== (userId ?? null) || Date.now() - parsed.savedAt > CHAT_DRAFT_TTL_MS) {
      window.localStorage.removeItem(CHAT_DRAFT_KEY);
      return null;
    }
    return parsed;
  } catch {
    try { window.localStorage.removeItem(CHAT_DRAFT_KEY); } catch {}
    return null;
  }
}

function writeChatDraft(state, userId) {
  const payload = { ...state, userId: userId ?? null, savedAt: Date.now() };
  try {
    window.localStorage.setItem(CHAT_DRAFT_KEY, JSON.stringify(payload));
  } catch {
    // Likely a quota error from a large base64 detected logo — retry without it
    // so the rest of the conversation + card still survive the reload.
    try {
      const lite = { ...payload };
      if (lite.siteCheck?.brand?.logo?.dataUrl) {
        lite.siteCheck = { ...lite.siteCheck, brand: { ...lite.siteCheck.brand, logo: null } };
      }
      window.localStorage.setItem(CHAT_DRAFT_KEY, JSON.stringify(lite));
    } catch {}
  }
}

function clearChatDraft() {
  try { window.localStorage.removeItem(CHAT_DRAFT_KEY); } catch {}
}

// Localized example chips shown before the AI has produced any suggestions
// yet (pristine chat + right after a boot/language reset). `.isDefault` tags
// the returned array so the render below can tell "these are the canned
// examples" apart from AI-suggested chips without a fragile reference-equality
// check (a new array is created on every call, e.g. every language change).
const defaultSuggestions = (t) => {
  const chips = [
    t('onboardingChat.chip1', { defaultValue: 'Boutique skincare brand — small batches, sustainable' }),
    t('onboardingChat.chip2', { defaultValue: 'B2B SaaS for property managers' }),
    t('onboardingChat.chip3', { defaultValue: "Wedding photographer — Paris & Côte d'Azur" }),
  ];
  chips.isDefault = true;
  return chips;
};

// Visual-style presets — must align with normalizeVisualStyle on the
// backend (onboarding-profile.ts). Order is the user-facing pick order.
const VISUAL_STYLE_PRESETS = [
  { key: 'premium_dark', label: 'Premium dark',  desc: 'Elegant, restrained, luxe',         from: '#0F1419', to: '#3A2E58' },
  { key: 'minimalist',   label: 'Minimalist',    desc: 'Clean, calm, lots of white space',  from: '#FAFAFA', to: '#E5E5E5' },
  { key: 'modern',       label: 'Modern glossy', desc: 'Sleek, contemporary, sharp',        from: '#5B7EFF', to: '#A555F5' },
  { key: 'vibrant',      label: 'Vibrant',       desc: 'Bold colors, high energy',          from: '#FF6F61', to: '#FFD66B' },
  { key: 'playful',      label: 'Playful',       desc: 'Friendly, quirky, warm',            from: '#FFB36B', to: '#FF7AA9' },
  { key: 'corporate',    label: 'Corporate',     desc: 'Formal, trustworthy, structured',   from: '#0E5BC1', to: '#1E40AF' },
  { key: 'vintage',      label: 'Vintage',       desc: 'Nostalgic, textured, classic',      from: '#8C6E4A', to: '#C99966' },
];

// Languages we can write content in. Keys are ISO 639-1 codes that
// match the backend `normalizeLanguage` output and Edit Strategy's
// ES_LANGUAGES list. Showing a structured picker is way better than
// asking the user to type "Ukrainian" and hoping it parses.
const LANGUAGE_PRESETS = [
  { code: 'en', name: 'English',    native: 'English'     },
  { code: 'uk', name: 'Ukrainian',  native: 'Українська'  },
  { code: 'ru', name: 'Russian',    native: 'Русский'     },
  { code: 'de', name: 'German',     native: 'Deutsch'     },
  { code: 'fr', name: 'French',     native: 'Français'    },
  { code: 'es', name: 'Spanish',    native: 'Español'     },
  { code: 'pt', name: 'Portuguese', native: 'Português'   },
  { code: 'it', name: 'Italian',    native: 'Italiano'    },
  { code: 'pl', name: 'Polish',     native: 'Polski'      },
  { code: 'cs', name: 'Czech',      native: 'Čeština'     },
  { code: 'nl', name: 'Dutch',      native: 'Nederlands'  },
  { code: 'lt', name: 'Lithuanian', native: 'Lietuvių'    },
  { code: 'lv', name: 'Latvian',    native: 'Latviešu'    },
  { code: 'sv', name: 'Swedish',    native: 'Svenska'     },
];

// Map the canonical backend field set (the minimum required to ship)
// to a flat completeness ratio for the progress bar. Honest progress
// beats turn-count progress — the user sees "60% filled" instead of
// "we're on turn 3 of 7" which conveys nothing about how close they are.
function profileFilledRatio(extracted) {
  if (!extracted) return 0;
  const checks = [
    Boolean(extracted.businessDescription || extracted.offer
      || (extracted.productOrService && (extracted.positioning || extracted.businessName))
      || (extracted.positioning && extracted.businessName)),
    Boolean(extracted.productOrService || extracted.offer),
    Boolean(extracted.targetAudience),
    Boolean(extracted.goals?.length),
    Boolean(extracted.primaryConversionAction),
    Boolean(extracted.preferredPlatforms?.length),
    Boolean(extracted.visualStyle),
    Boolean(extracted.contentBoundaries?.length || extracted.forbiddenTopics?.length),
  ];
  return checks.filter(Boolean).length / checks.length;
}

export function Onboarding({ onComplete }) {
  const { user } = useAuth() || {};
  return <OnboardingSession key={user?.id ?? 'anon'} onComplete={onComplete} />;
}

function OnboardingSession({ onComplete }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth() || {};
  const dashboardData = useDashboardData();
  const brand = useBrand();
  const mounted = useRef(false);
  const finishing = useRef(false);
  const handedOff = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const firstName = (user?.name || user?.email || '').split(/[\s@]/)[0] || '';
  const quickMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('mode') === 'quick';
  const trialSetup = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('source') === 'trial-setup';
  const freePreviewSetup = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('source') === 'free-preview';
  const [quickBrandOpen, setQuickBrandOpen] = useState(quickMode);

  // Restore an in-progress chat (read once at mount). Account-scoped so a
  // different user on this browser never inherits it. RequireAuth guarantees
  // `user` is loaded before we mount, so the id is reliable here.
  const chatDraft = useRef(readChatDraft(user?.id)).current;
  const startPromiseRef = useRef(null);
  const [isReentry, setIsReentry] = useState(Boolean(chatDraft));

  // Funnel (TASK-122): fired once per genuinely fresh mount — a resumed
  // draft (chatDraft present) means this browser already started the chat
  // earlier, so it doesn't count as a new "started" for the funnel. Reads
  // chatDraft's initial ref value only (no dependency drift across renders).
  useEffect(() => {
    if (!quickMode && !chatDraft) {
      try { track('onboarding_started'); } catch { /* fail-silent */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chat history — array of { kind: 'bot'|'user', text }
  const [history, setHistory] = useState(() => chatDraft?.history ?? []);
  // Current drip messages (not yet committed to history)
  const [currentBot, setCurrentBot] = useState(() => chatDraft?.currentBot ?? buildGreeting(firstName, t));
  // Current quick-pick chips
  const [chips, setChips] = useState(() => chatDraft?.chips ?? defaultSuggestions(t));
  // Sticky input value
  const [input, setInput] = useState('');
  // Guards the one-time landing-hero site prefill (see effect below) against
  // React 18 StrictMode's dev double-invoke of effects.
  const prefillSiteConsumed = useRef(false);
  // The consumed ?site= value survives here so a boot-time draft invalidation
  // (stale draft dropped because the server opened a NEW session) can still
  // apply it — consumeSitePrefill() clears the storage key on first read.
  const consumedPrefillRef = useRef(null);
  // Explainer chip above the prefilled site ("use this as your brand source?
  // Send = yes, edit = no"). Cleared on send or when the user edits the value.
  const [sitePrefillHint, setSitePrefillHint] = useState(false);
  // Network state
  const [sending, setSending] = useState(false);
  const [bootError, setBootError] = useState(null);
  const [sendError, setSendError] = useState(null);
  // Server-driven progress + state
  const [serverState, setServerState] = useState(() => ({
    ...(chatDraft?.serverState ?? {
      currentStep: 'business',
      turnCount: 0,
      maxTurns: 8,
      canComplete: false,
    }),
    // A restored draft has no in-flight completion promise to wait for.
    completing: false,
  }));
  // Last AI-extracted profile fields — used to populate the celebration pills
  const [lastExtracted, setLastExtracted] = useState(() => chatDraft?.lastExtracted ?? null);
  // Website auto-analysis: { status: 'loading'|'ready'|'applied', url, host, result }.
  // Triggered when a user message contains a fetchable link; runs in
  // parallel with the AI turn and silently disappears on any failure.
  // Restore a still-actionable 'found' intake card across reload. Transient
  // statuses ('detecting' shimmer, 'applied' pill) are NOT revived — the scan
  // promise is gone — so they drop to null.
  const [siteCheck, setSiteCheck] = useState(() => {
    const s = chatDraft?.siteCheck;
    return s && s.status === 'found' ? s : null;
  });
  const [siteApplying, setSiteApplying] = useState(false);
  // Hosts we already analyzed this conversation (persisted with the chat
  // draft) — one shot per host so a dismissed card never nags again.
  const analyzedHostsRef = useRef(new Set(Array.isArray(chatDraft?.analyzedHosts) ? chatDraft.analyzedHosts : []));
  // Post-onboarding activation overlay (plan → celebrate → connect → first post)
  const [activating, setActivating] = useState(() => {
    if (typeof window === 'undefined') return false;
    // A post-trial invitation starts with the existing interview, never a
    // previously interrupted activation chain. Completing it remains explicit.
    if (quickMode || trialSetup) return false;
    // 1) OAuth round-trip — App.jsx forwards `?continue=activation`
    //    back to /onboarding when a social-connect OAuth flow returns.
    const params = new URLSearchParams(window.location.search);
    if (params.get('continue') === 'activation') return true;
    // 2) Recovery from refresh mid-activation, but ONLY if the stash
    //    is fresh (≤ 2h). Old stashes from previous sessions get
    //    dropped so a broken state never blocks the user across
    //    days/deploys. Reaches into the activation stash file via
    //    a direct localStorage read so we don't import a circular
    //    dependency. Keep these in sync with OnboardingActivation.
    try {
      const raw = window.localStorage.getItem('dathent_onboarding_activation_state');
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (!parsed?.phase) return false;
      if (typeof parsed.savedAt !== 'number') {
        // Pre-TTL stash — drop it.
        window.localStorage.removeItem('dathent_onboarding_activation_state');
        return false;
      }
      // Account-scoped — never resume a DIFFERENT account's activation state
      // on a shared browser (the reported bug: a new account landed on the
      // previous account's "Connect accounts" step). RequireAuth guarantees
      // `user` is loaded before onboarding mounts, so this id is reliable.
      if (parsed.userId !== (user?.id ?? null)) {
        window.localStorage.removeItem('dathent_onboarding_activation_state');
        return false;
      }
      if (Date.now() - parsed.savedAt > 2 * 60 * 60 * 1000) {
        window.localStorage.removeItem('dathent_onboarding_activation_state');
        return false;
      }
      return true;
    } catch {
      try { window.localStorage.removeItem('dathent_onboarding_activation_state'); } catch {}
      return false;
    }
  });

  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Drip the current bot turn
  const { shown, typing } = useBotTurn(currentBot, () => {
    // After bot finishes dripping, focus the input
    inputRef.current?.focus();
  });

  // Landing-hero site prefill: /login?site=<url> is stashed by Auth.jsx under
  // PREFILL_SITE_KEY; this is the URL-first greeting's moment to use it.
  // consumeSitePrefill() ALWAYS clears the key (regardless of whether we
  // apply it below) so it can never leak into a later session or a different
  // flow. Only applied to a genuinely fresh chat — a resumed draft (chatDraft)
  // means the user is already past the "have a link?" step — and never
  // overwrites text the user already typed. No auto-send: this only fills
  // the textbox, the user still has to hit Send.
  useEffect(() => {
    if (prefillSiteConsumed.current) return;
    prefillSiteConsumed.current = true;
    const prefill = consumeSitePrefill();
    if (!prefill) return;
    consumedPrefillRef.current = prefill;
    if (chatDraft) return;
    setInput((cur) => {
      if (cur) return cur;
      // Show the explainer chip only when the prefill actually landed — it
      // answers "is this the site you want as your branding source?" without
      // an extra click: Send = yes, edit = no.
      setSitePrefillHint(true);
      return prefill;
    });
  }, [chatDraft]);

  // Boot: start session + resume if there's prior history
  useEffect(() => {
    if (quickMode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // Reuse one promise across React StrictMode's effect replay. Otherwise
        // the first call creates a fresh session and the replay immediately
        // sees it as `reused`, incorrectly exposing Close on a true first entry.
        startPromiseRef.current ||= api.onboarding.start();
        const started = await startPromiseRef.current;
        if (cancelled) return;
        // A lifetime completion receipt is a result, not a new AI session.
        // Do not replay activation (or its legacy generation) on re-entry.
        if (started?.completed === true) {
          clearChatDraft();
          handleActivationDone();
          return;
        }
        setIsReentry(started?.reused === true);
        // The server opened a BRAND-NEW session (`reused: false`) — the prior
        // one expired, completed, or was reset server-side. Any restored local
        // draft narrates a conversation the server no longer knows: replaying
        // it resurrects stale scan cards (old logo/colors) and answers that
        // were never saved to THIS session. Drop it and restart from the
        // greeting. `reused: true` keeps today's resume behavior unchanged.
        if (started?.reused === false && chatDraft) {
          clearChatDraft();
          analyzedHostsRef.current = new Set();
          setHistory([]);
          setCurrentBot(buildGreeting(firstName, t));
          setChips(defaultSuggestions(t));
          setServerState({ currentStep: 'business', turnCount: 0, maxTurns: 8, canComplete: false, completing: false });
          setLastExtracted(null);
          setSiteCheck(null);
          // A landing-hero ?site= prefill consumed on mount was skipped because
          // the draft existed; the draft is gone now, so apply it after all.
          const prefill = consumedPrefillRef.current;
          if (prefill) {
            setInput((cur) => {
              if (cur) return cur;
              setSitePrefillHint(true);
              return prefill;
            });
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[onboarding] start failed', err);
        setBootError(err?.message || t('onboardingChat.errorStartSession'));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Rebuild the greeting + example chips when the interface language changes
  // (the onboarding language picker). Same guard as the boot-reset above —
  // ONLY while the chat is still pristine (no history yet). A restored draft
  // means history is already non-empty, so it's untouched by a later language
  // switch here; this only refreshes the very first, not-yet-sent turn.
  useEffect(() => {
    if (history.length !== 0) return;
    setCurrentBot(buildGreeting(firstName, t));
    setChips(defaultSuggestions(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Show the visual-style picker when the AI is asking about visual
  // style OR when the AI's most recent message asks about it inline
  // (the AI sometimes combines platforms + visual style into one
  // question). Detection: server step is visual_style OR latest
  // extracted is missing visualStyle AND the visible bot message
  // mentions visual style.
  const latestBotText = (currentBot?.[currentBot.length - 1] || '').toLowerCase();
  const aiAskingAboutVisuals =
    /visual\s+style|стил[ьеіи]|віжуал|визуал/i.test(latestBotText) ||
    /mood|moodboard|aesthetic|естетик|естетич/i.test(latestBotText);
  const visualStyleMissing = !lastExtracted?.visualStyle;
  const showVisualStylePicker =
    !serverState.canComplete && (
      serverState.currentStep === 'visual_style' ||
      (visualStyleMissing && aiAskingAboutVisuals)
    );
  // Same trick for language: show the structured picker when the AI
  // is on the `language` step OR when language is missing AND the
  // bot is asking about it inline. The pattern must match QUESTIONS only —
  // a bare \blanguage also caught acknowledgments ("Noted your primary
  // content language is Ukrainian…") and re-summoned the picker after the
  // user had already answered.
  const aiAskingAboutLanguage =
    /(what|which)\s+language|language should|language do you|written in\?|якою мовою|яку мову|на каком языке|какой язык/i.test(latestBotText);
  const languageMissing = !lastExtracted?.language;
  const showLanguagePicker =
    !serverState.canComplete && !showVisualStylePicker && languageMissing && (
      serverState.currentStep === 'language' || aiAskingAboutLanguage
    );

  // Persist the visible chat so a refresh (or an OAuth round-trip) resumes
  // the conversation instead of dropping back to the greeting. Skip the
  // pristine greeting (history empty) and stop once we've handed off to
  // activation — finish() clears the draft at that point.
  useEffect(() => {
    if (handedOff.current || activating || history.length === 0) return;
    // Persist a 'found' intake card so a reload keeps it (other statuses are
    // transient — don't stash a shimmer or an already-applied pill).
    const persistSite = siteCheck && siteCheck.status === 'found' ? siteCheck : null;
    writeChatDraft({ history, currentBot, chips, serverState, lastExtracted, analyzedHosts: [...analyzedHostsRef.current], siteCheck: persistSite }, user?.id);
  }, [history, currentBot, chips, serverState, lastExtracted, activating, user?.id, siteCheck]);

  // Auto-scroll to the newest message. The drip reveals bubbles by flipping
  // `typing` and `shown.length` rapidly; with behavior:'smooth' the successive
  // scroll animations stacked and the browser canceled them mid-flight, so the
  // thread kept settling ABOVE the latest bubble — the "chat never reaches the
  // bottom, have to scroll by hand" bug. Use an INSTANT jump to scrollHeight:
  // it lands exactly at the bottom every time and can't be interrupted by the
  // next drip tick. The 120ms debounce batches a burst into one write; the
  // double-RAF lets the new bubble lay out before we read scrollHeight.
  useEffect(() => {
    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const stage = scrollRef.current;
          if (!stage) return;
          stage.scrollTop = stage.scrollHeight;
        });
      });
    }, 120);
    return () => clearTimeout(timer);
  }, [
    shown.length,
    typing,
    sending,
    history.length,
    chips.length,
    serverState.canComplete,
    // Re-anchor after a short iOS viewport gains or loses a tall picker.
    showLanguagePicker,
    showVisualStylePicker,
    // The intake card mounts/fills async (detecting → found) and is TALL —
    // re-run the scroll when its state flips so its CTA lands in view.
    siteCheck?.status,
  ]);

  // Send a turn — either from textarea or chip click. `stepOverride` lets a
  // STRUCTURED picker attribute its answer to its own step regardless of the
  // server's linear step: an INLINE picker (shown via the AI's question text
  // while the server step is e.g. 'platforms') otherwise has its answer parsed
  // as the wrong field on the worker, the real field stays missing, and the
  // picker re-summons — the "asks language twice" bug.
  const send = async (text, stepOverride) => {
    const value = (text || '').trim();
    if (!value || sending) return;
    setSendError(null);
    setSitePrefillHint(false);
    setSending(true);

    // 1) Commit the current bot drip to history so it doesn't disappear
    if (currentBot.length > 0) {
      setHistory((h) => [
        ...h,
        ...currentBot.map((t) => ({ kind: 'bot', text: t })),
        { kind: 'user', text: value },
      ]);
    } else {
      setHistory((h) => [...h, { kind: 'user', text: value }]);
    }
    setCurrentBot([]);
    setChips([]);
    setInput('');

    // Fire-and-forget: if the message contains a fetchable link, analyze it
    // in parallel. Never blocks or alters the normal AI turn below.
    maybeAnalyzeWebsite(value);

    try {
      const res = await api.onboarding.sendAiMessage({
        answerText: value,
        currentStep: stepOverride || serverState.currentStep,
        uiLanguage: String(i18n.language || 'en').slice(0, 2),
      });
      const data = res || {};
      const nextBot = (data.assistantMessage || '').trim();
      const nextChips = Array.isArray(data.suggestedAnswers) ? data.suggestedAnswers : [];
      setCurrentBot(nextBot ? splitParagraphs(nextBot) : [t('onboardingChat.fallbackGotIt')]);
      setChips(nextChips);
      // ACCUMULATE across turns — replacing wholesale forgot fields answered
      // earlier (e.g. language), which re-summoned pickers the user had
      // already answered.
      setLastExtracted((cur) => ({ ...(cur || {}), ...(data.extracted || {}) }));
      // Belt-and-braces for the "asks language twice" bug: when this turn
      // answered the LANGUAGE step but the model returned the next question
      // WITHOUT echoing `language` in `extracted` (and without advancing
      // suggestedNextStep), currentStep stays stuck on 'language' and the
      // structured language picker re-appears even though the user just
      // answered. Record the language locally so the picker stays gone.
      // complete-onboarding normalizes the raw text to an ISO code, so it's safe.
      if (serverState.currentStep === 'language' && !(data.extracted && data.extracted.language)) {
        setLastExtracted((cur) => ({ ...(cur || {}), language: (cur && cur.language) || value }));
      }
      const nextStep = data.suggestedNextStep || serverState.currentStep;
      setServerState({
        currentStep: nextStep,
        turnCount: typeof data.turnCount === 'number' ? data.turnCount : serverState.turnCount + 1,
        maxTurns: typeof data.maxTurns === 'number' ? data.maxTurns : serverState.maxTurns,
        canComplete: Boolean(data.canComplete),
        completing: false,
      });
      // Funnel (TASK-122): the owner's diagnosis names the `business` step as
      // the biggest drop-off — step_key is the step the user just ANSWERED
      // (not the one they're headed into), so a funnel breakdown by step_key
      // directly shows where turns stop happening.
      try { track('onboarding_step', { step_key: serverState.currentStep, next_step: nextStep }); } catch { /* fail-silent */ }
    } catch (err) {
      console.error('[onboarding] sendAiMessage failed', err);
      setSendError(err?.message || t('onboardingChat.errorReachDathent'));
      // Restore the user's text so they can retry
      setInput(value);
      setHistory((h) => h.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  // Detect a fetchable link in the user's message and analyze it in the
  // background. All failure paths are silent no-ops — the chat flow is
  // exactly the same as before unless we have something useful to show.
  const maybeAnalyzeWebsite = (text) => {
    if (serverState.canComplete) return;
    if (siteCheck && siteCheck.status !== 'applied') return; // a card is already up
    const found = extractWebsiteUrlFromText(text);
    if (!found || analyzedHostsRef.current.has(found.host)) return;
    analyzedHostsRef.current.add(found.host);

    // Flag OFF (default / SHIP DARK) — current prod behavior: the website-fill
    // card only (business profile, no logo). Byte-identical to today.
    if (!intakeCardEnabled()) {
      setSiteCheck({ status: 'loading', url: found.href, host: found.host, result: null });
      api.onboarding.analyzeWebsite({ url: found.href })
        .then((result) => {
          const patch = cleanSitePatch(result?.suggestedProfilePatch);
          const informative = Object.keys(patch).filter((k) => k !== 'websiteUrl' && k !== 'websiteAnalysis').length;
          if (informative < 2) { setSiteCheck(null); return; }
          setSiteCheck({ status: 'ready', url: result?.url || found.href, host: found.host, result: { patch, confidence: result?.confidence } });
        })
        .catch(() => { setSiteCheck(null); });
      return;
    }

    // Flag ON — unified intake: the business-profile scan AND the brand
    // (logo/colors) scan run in parallel; the card shows a shimmer until BOTH
    // settle, then merges whatever each returned. Either failing is fine.
    setSiteCheck({ status: 'detecting', url: found.href, host: found.host, domain: found.host, business: null, brand: null });

    const wsP = api.onboarding.analyzeWebsite({ url: found.href })
      .then((r) => ({ patch: cleanSitePatch(r?.suggestedProfilePatch), confidence: r?.confidence, url: r?.url }))
      .catch(() => null);
    const bdP = api.onboarding.detectBrand({ url: found.href, brandName: lastExtracted?.businessName || undefined })
      .then(async (r) => {
        if (!r?.found) return null;
        let logo = r.logo?.dataUrl ? { dataUrl: r.logo.dataUrl } : null;
        // A detected SVG (the most common real-logo format — site headers ship
        // Logo.svg) is rasterized to PNG right here, client-side: our servers
        // reject raw SVG uploads by design (see lib/rasterize.js), and this is
        // the exact same path the Brand Kit picker uses for user-picked .svg
        // files. Rasterization failure (tainted canvas etc.) falls back to no
        // logo rather than a broken card.
        if (logo && ((r.logo.mimeType || '').includes('svg'))) {
          try {
            const svgBlob = await (await fetch(logo.dataUrl)).blob();
            const png = await rasterizeSvgToPng(svgBlob);
            const pngDataUrl = await new Promise((resolve, reject) => {
              const fr = new FileReader();
              fr.onload = () => resolve(fr.result);
              fr.onerror = reject;
              fr.readAsDataURL(png);
            });
            logo = { dataUrl: pngDataUrl };
          } catch { logo = null; }
        }
        return {
          logo,
          colors: Array.isArray(r.colors) ? r.colors : [],
          styleTags: Array.isArray(r.styleTags) ? r.styleTags : [],
          domain: r.domain || found.host,
        };
      })
      .catch(() => null);

    Promise.all([wsP, bdP]).then(([ws, bd]) => {
      const patch = ws?.patch || {};
      const informative = Object.keys(patch).filter((k) => k !== 'websiteUrl' && k !== 'websiteAnalysis').length;
      const hasBrand = Boolean(bd && (bd.logo || (bd.colors && bd.colors.length)));
      // Nothing usable from either scan → silently drop the card (no nagging).
      if (informative < 1 && !hasBrand) { setSiteCheck(null); return; }
      setSiteCheck((prev) => (prev && prev.status === 'detecting' ? {
        status: 'found',
        url: ws?.url || found.href,
        host: found.host,
        domain: (bd && bd.domain) || found.host,
        confidence: ws?.confidence,
        business: patch,
        brand: bd || { logo: null, colors: [], styleTags: [] },
      } : prev));
    });
  };

  // Confirm the reviewed intake card. Persists BOTH the brand identity
  // (logo → reference upload, colors → profile patch) AND the accepted
  // business-profile fields via the same onboarding-answer mechanism the
  // AI turn uses, so the next turn sees the enriched profile. Confirm-first:
  // only the kept/edited values the card emits are written.
  const confirmIntake = async ({ logo, colors, fields }) => {
    if (!siteCheck || siteApplying) return;
    setSiteApplying(true);
    try {
      // Logo first — server-side persist (R2 + media_assets + sets logoAssetId
      // on the onboarding answer). Best-effort: a failure must not block fields.
      if (logo?.dataUrl) {
        await api.onboarding.addReference({ type: 'logo', fileName: 'brand-logo.png', dataUrl: logo.dataUrl })
          .catch((err) => { console.error('[onboarding] logo persist failed', err); });
      }
      // Business-profile patch from the (kept/edited) fields. Array fields are
      // split back from their "·"-joined display strings.
      const patch = {};
      ['businessDescription', 'offer', 'targetAudience', 'toneOfVoice', 'productOrService', 'visualStyle']
        .forEach((k) => { if (fields[k] && fields[k].trim()) patch[k] = fields[k].trim(); });
      if (fields.contentPillars) patch.contentPillars = splitList(fields.contentPillars);
      if (fields.goals) patch.goals = splitList(fields.goals);
      if (fields.painPoints) patch.painPoints = splitList(fields.painPoints);
      if (Array.isArray(colors) && colors.length) patch.brandColors = colors;

      if (Object.keys(patch).length) {
        await api.onboarding.saveMessage({
          stepKey: 'website_analysis',
          question: 'Site scan reviewed & confirmed',
          answerText: `Confirmed ${siteCheck.url}`,
          extracted: { ...patch, sourceMetadata: websiteSourceMetadata(patch, siteCheck.confidence) },
        });
      }
      // Local merge so the progress bar jumps immediately. Chat-extracted
      // values win over scanned values — the user's own words are truth.
      setLastExtracted((cur) => ({ ...patch, ...(cur || {}) }));
      setSiteCheck((s) => (s ? { ...s, status: 'applied' } : null));
    } catch (err) {
      console.error('[onboarding] intake confirm failed', err);
      setSendError(err?.message || t('onboardingChat.siteApplyError'));
    } finally {
      setSiteApplying(false);
    }
  };

  const dismissIntake = () => setSiteCheck(null);
  // "Tell me about your business" on the not-found card — drop the card and
  // hand focus back to the chat input so the user can describe it directly.
  const tellAboutBusiness = () => { setSiteCheck(null); setTimeout(() => inputRef.current?.focus(), 40); };

  // Flag-OFF apply path — the legacy WebsiteInsightCard's "Apply" (business
  // fields only, no logo). Kept so the dark/default behavior is unchanged.
  const applySiteSuggestion = async () => {
    if (!siteCheck || siteCheck.status !== 'ready' || siteApplying) return;
    const { patch, confidence } = siteCheck.result;
    setSiteApplying(true);
    try {
      await api.onboarding.saveMessage({
        stepKey: 'website_analysis',
        question: 'Website profile suggestions accepted',
        answerText: `Analyzed ${siteCheck.url}`,
        extracted: { ...patch, sourceMetadata: websiteSourceMetadata(patch, confidence) },
      });
      setLastExtracted((cur) => ({ ...patch, ...(cur || {}) }));
      setSiteCheck({ status: 'applied', url: siteCheck.url, host: siteCheck.host, result: null });
    } catch (err) {
      console.error('[onboarding] website apply failed', err);
      setSendError(err?.message || t('onboardingChat.siteApplyError'));
    } finally {
      setSiteApplying(false);
    }
  };

  // The applied pill auto-dismisses after a beat.
  useEffect(() => {
    if (siteCheck?.status !== 'applied') return;
    const timer = setTimeout(() => setSiteCheck(null), 2600);
    return () => clearTimeout(timer);
  }, [siteCheck?.status]);

  const finish = async () => {
    if (handedOff.current || finishing.current || !mounted.current) return;
    finishing.current = true;
    setSendError(null);
    setServerState((s) => ({ ...s, completing: true }));
    try {
      // Research-first flow: completion also kicks the free lite research run
      // server-side; the dashboard Setup Journey picks the chain up from there.
      // Do not infer a completion brandProfileId from the active-brand cache.
      // Enriching an owned bare workspace needs an agreed server contract;
      // the existing completion endpoint remains responsible for ownership.
      const result = await api.onboarding.complete(researchOnboardingEnabled() ? { researchKickoff: true } : {});
      if (!mounted.current) return;
      // HTTP 202 is a successful transport response, not completed onboarding.
      // Keep the interview and retry only this idempotent completion request.
      if (result?.pending === true || result?.completed === false) {
        throw new Error(t('onboardingChat.completionPending'));
      }
      if ((dashboardData.entitlements?.accessState === 'free' || freePublishingState(dashboardData.entitlements))
        && (result?.researchStarted === false || result?.activation === 'research_not_started')) {
        throw new Error(t('onboardingChat.researchNotStarted'));
      }
      clearChatDraft();
      // The dashboard provider and API client both cache these probes. Refresh
      // now so a completed profile cannot leave the Finish Setup card visible.
      const [entitlementsRefresh] = await Promise.allSettled([
        dashboardData.invalidateResource?.('entitlements', { fresh: true, ...(freePreviewSetup ? { throwOnError: true } : {}) }),
        dashboardData.invalidateResource?.('setupStatus'),
        dashboardData.invalidateResource?.('socialAccounts'),
        brand.refresh?.(),
      ]);
      if (!mounted.current) return;
      // Reuse the existing scoped mark-read endpoint rather than adding a
      // second notification mutation contract. This only runs after the brand
      // was saved successfully; failures stay unread for the next retry.
      try {
        const response = await api.notifications.list({ status: 'unread', limit: 100 });
        if (!mounted.current) return;
        const notifications = response?.data?.notifications || response?.notifications || [];
        await Promise.all(notifications
          .filter((notification) => notification?.type === 'finish_brand')
          .map((notification) => api.notifications.markRead(notification.id)));
      } catch (_) { /* reminder cleanup is best-effort; brand creation still succeeded */ }
      if (!mounted.current) return;
      // The explicit one-time Free interview has already started research.
      // Land on its existing progress/results board, not the legacy connect
      // and celebration chain. A URL or stale cached grant is not enough.
      const freshEntitlements = entitlementsRefresh.status === 'fulfilled' ? entitlementsRefresh.value : null;
      if (freePreviewSetup && freePreviewClientEnabled()
        && freePublishingState(freshEntitlements)
        && freePreviewState(freshEntitlements)?.mode === 'active') {
        handedOff.current = true;
        clearChatDraft();
        onComplete?.('dashboard?preview=1', { onboardingCompleted: true });
        return;
      }
      setActivating(true);
    } catch (err) {
      if (!mounted.current) return;
      console.error('[onboarding] complete failed', err);
      setSendError(err?.message || t('onboardingChat.errorReachDathent'));
      setServerState(s => ({ ...s, completing: false }));
    } finally {
      finishing.current = false;
    }
  };

  const handleActivationDone = ({ firstPostId, tour, gotoBilling } = {}) => {
    if (!mounted.current) return;
    const preview = freePreviewClientEnabled() && freePreviewState(dashboardData.entitlements);
    if (!gotoBilling && freePublishingState(dashboardData.entitlements) && preview && preview.mode !== 'hidden') {
      onComplete?.('dashboard?preview=1', { onboardingCompleted: true });
      return;
    }
    // User chose "Choose a plan" from the first-post-ready screen —
    // route them to billing/plans. The first post stays in their library
    // either way; they can come back to it from the dashboard.
    if (gotoBilling) {
      onComplete?.('billing', { onboardingCompleted: true });
      return;
    }
    // If we have a generated first post, route the user straight to its
    // preview — that's the most satisfying landing after onboarding.
    // Otherwise fall back to dashboard (with tour if user opted in).
    if (firstPostId) {
      onComplete?.(`post:${firstPostId}`, { onboardingCompleted: true });
      return;
    }
    onComplete?.(tour ? 'dashboard?tour=1' : 'dashboard', { onboardingCompleted: true });
  };

  const leaveOnboarding = () => {
    // Skip and re-entry Close/Escape are all explicit exits. Persist before
    // navigating so RequireOnboarding cannot bounce this account straight back.
    markOnboardingSkipped(user?.id);
    // invalidateResource evicts both context and API-client caches before its
    // request starts. Navigation need not wait; the dashboard mount force-read
    // deduplicates against these requests and paints the current setup state.
    dashboardData.invalidateResource?.('setupStatus');
    dashboardData.invalidateResource?.('socialAccounts');
    onComplete?.('dashboard', { onboardingCompleted: false });
  };

  const closeOnboarding = leaveOnboarding;

  useEffect(() => {
    if (!isReentry || activating || serverState.completing) return undefined;
    const onEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeOnboarding();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [isReentry, activating, serverState.completing, onComplete]);

  // Always-available escape hatch. Onboarding hides the app chrome (no
  // sidebar/topbar menu), so without this a user has no way to sign out —
  // e.g. when testing multiple accounts, or if they land here by mistake.
  // logout() clears the session; RequireAuth then bounces them to /login.
  const exitBtn = (
    <button
      type="button"
      className="ob-top-action ob-logout"
      onClick={async () => { try { await logout?.(); } catch (e) {} }}
      title={t('onboardingChat.logoutTitle')}
    >
      <I.logout size={13} /> {t('onboardingChat.logout')}
    </button>
  );

  // Onboarding is recommended, not a trap: this prominent action is available
  // at every step. The account-scoped marker makes the destination terminal.
  const skipBtn = (
    <button
      type="button"
      className="ob-top-action ob-skip"
      onClick={() => {
        try { track('onboarding_skipped', { step: serverState.currentStep, surface: 'chat', source: getFunnelSource() }); } catch { /* fail-silent */ }
        leaveOnboarding();
      }}
    >
      <I.clock size={13} /> {t('onboarding.skipForNow')}
    </button>
  );

  if (activating) {
    return (
      <>
        <div className="ob-corner-actions">{exitBtn}</div>
        <OnboardingActivation
          firstName={firstName}
          extracted={lastExtracted}
          onDone={handleActivationDone}
          freshSetup={trialSetup}
        />
      </>
    );
  }

  const submit = (e) => {
    e?.preventDefault?.();
    send(input);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // Progress = max(profile-completeness, turn-progress). Completeness is
  // the honest signal — it tells the user how close they are to a usable
  // profile, not how many messages they've sent. We OR with turn progress
  // so a slow chatter still sees the bar move.
  const filled = profileFilledRatio(lastExtracted);
  const turnProgress = (serverState.turnCount + 1) / Math.max(1, serverState.maxTurns);
  const progressPct = Math.min(100, Math.round(Math.max(filled, turnProgress) * 100));
  const stepLabel = humanStep(serverState.currentStep, t);

  // Build the unified intake card's data from BOTH scans + interview answers.
  // Website scan covers what-you-do / audience / tone / pillars; brand scan
  // covers logo + colors; platforms/goals/pain are interview-sourced (the scan
  // never returns them) so we pull them from lastExtracted when present.
  const intakeData = (intakeCardEnabled() && siteCheck) ? {
    domain: siteCheck.domain || siteCheck.host || '',
    logo: siteCheck.brand?.logo || null,
    colors: siteCheck.brand?.colors || [],
    styleTags: siteCheck.brand?.styleTags || [],
    business: {
      businessDescription: siteCheck.business?.businessDescription || '',
      productOrService: siteCheck.business?.productOrService || '',
      offer: siteCheck.business?.offer || '',
      targetAudience: siteCheck.business?.targetAudience || '',
      painPoints: joinList(lastExtracted?.painPoints),
      toneOfVoice: siteCheck.business?.toneOfVoice || '',
      contentPillars: joinList(siteCheck.business?.contentPillars),
      goals: joinList(lastExtracted?.goals),
      visualStyle: siteCheck.business?.visualStyle || lastExtracted?.visualStyle || '',
      platforms: Array.isArray(lastExtracted?.preferredPlatforms) ? lastExtracted.preferredPlatforms : [],
    },
  } : null;

  return (
    <div className="ob-root">
      <header className="ob-top">
        <a href="/app" className="ob-brand">
          <img src="/logo.png" alt="" width="28" height="28" style={{ borderRadius: 6, display: 'block' }} />
          <span>Dathent</span>
          <span className="ob-brand-sub mono">{t('onboardingChat.brandSub')}</span>
        </a>

        <div className="ob-progress">
          <div className="ob-progress-meta">
            <span className="mono">{stepLabel}</span>
            <span className="mono">{progressPct}%</span>
          </div>
          <div className="ob-progress-bar">
            <div className="ob-progress-fill" style={{ width: progressPct + '%' }} />
          </div>
        </div>

        <div className="ob-top-r">
          {history.length === 0 && <OnboardingLangPicker />}
          {skipBtn}
          {isReentry && (
            <button
              type="button"
              className="btn btn-ghost btn-icon ob-close"
              onClick={closeOnboarding}
              title={t('onboarding.close')}
              aria-label={t('onboarding.closeLabel')}
              disabled={serverState.completing}
            >
              <I.x size={16} />
            </button>
          )}
          {exitBtn}
        </div>
      </header>

      <div className="ob-stage" ref={scrollRef}>
        <div className="ob-bg-glow" />
        <div className="ob-thread">
          {/* Committed history */}
          {history.map((h, i) => h.kind === 'bot'
            ? <BotMessage key={i} text={h.text} withAvatar={i === 0 || history[i - 1]?.kind !== 'bot'} />
            : <UserAnswer key={i} text={h.text} />
          )}

          {/* Current bot turn — dripping in */}
          {shown.map((b, bi) => (
            <React.Fragment key={`cur-${bi}`}>
              <BotMessage text={b} withAvatar={bi === 0} />
              {bi === 0 && history.length === 0 && !quickMode && (
                <button type="button" className="ob-no-brand-action" onClick={() => setQuickBrandOpen(true)}>
                  {t('quickBrand.noBrandAction')}
                </button>
              )}
            </React.Fragment>
          ))}
          {/* Show typing bubble while either dripping current turn OR
              waiting for the next AI response after the user sent. */}
          {(typing || sending) && <TypingBubble />}

          {/* Unified "we scanned your site" review card — drops inline in the
              thread (not the dock; a tall confirm-first card needs the scroller).
              Merges the website-profile scan + the brand/logo scan. Flag-gated
              (SHIP DARK): when off, the legacy dock card below renders instead. */}
          {intakeCardEnabled() && siteCheck && (
            siteCheck.status === 'applied' ? (
              <div className="ob-ic-applied"><I.check size={14} /> {t('onboardingChat.intake.applied')}</div>
            ) : (
              <div className="ob-ic-wrap">
                <IntakeCard
                  status={siteCheck.status}
                  data={intakeData}
                  busy={siteApplying}
                  onConfirm={confirmIntake}
                  onSkip={dismissIntake}
                  onTellAbout={tellAboutBusiness}
                />
              </div>
            )
          )}

          {bootError && (
            <div className="auth-error" style={{ alignSelf: 'center', maxWidth: 540 }}>
              <I.alert size={14} /> <span>{t('onboardingChat.sessionError', { error: bootError })}</span>
            </div>
          )}
          {sendError && (
            <div className="auth-error" style={{ alignSelf: 'center', maxWidth: 540 }}>
              <I.alert size={14} /> <span>{sendError}</span>
            </div>
          )}
          {/* Invisible scroll sentinel. scrollIntoView on this element
              moves the viewport so the user always sees the latest
              message + dock without manually scrolling. */}
          <div ref={bottomRef} className="ob-thread-end" aria-hidden="true" />
        </div>
      </div>

      <div className="ob-dock">
        <div className="ob-dock-inner">
          {serverState.canComplete ? (
            <div className="ob-in">
              <div className="ob-in-foot">
                <span className="ob-in-meta">{t('onboardingChat.profileReady')}</span>
                <button type="button" className="btn btn-primary btn-sm"
                        disabled={serverState.completing}
                        onClick={finish}>
                  {serverState.completing ? t('onboardingChat.entering') : t('onboardingChat.enterWorkspace')} <I.arrowRight size={12} />
                </button>
              </div>
            </div>
          ) : (
            <form className="ob-in ob-in-text" onSubmit={submit}>
              {/* Legacy website-fill card (flag OFF / default). When the intake
                  flag is ON the richer review card renders inline in the thread
                  above instead, so this is suppressed to avoid a double-render. */}
              {!intakeCardEnabled() && siteCheck && (
                <WebsiteInsightCard
                  check={siteCheck}
                  busy={siteApplying}
                  onApply={applySiteSuggestion}
                  onDismiss={() => setSiteCheck(null)}
                />
              )}
              {/* Structured pickers — render above textbox so the user
                  sees them first. Visual + language are mutually
                  exclusive (boolean derivation enforces that). */}
              {showVisualStylePicker && (
                <VisualStylePicker
                  disabled={sending}
                  onConfirm={(keys) => {
                    const labels = keys.map((k) => VISUAL_STYLE_PRESETS.find((p) => p.key === k)?.label || k);
                    // Attribute to the visual_style step even when the picker was
                    // shown INLINE (server step still elsewhere) — same class of
                    // bug as the language double-ask.
                    send(`${labels.join(', ')} (${keys.join(', ')})`, 'visual_style');
                  }}
                />
              )}
              {showLanguagePicker && (
                <LanguagePicker
                  disabled={sending}
                  onPick={(text, code) => {
                    // Optimistically record the picked language so the picker can
                    // NEVER re-summon — independent of whether the worker echoes
                    // it back this turn. Then send attributed to the 'language'
                    // step so the worker reliably extracts + stops re-asking.
                    if (code) setLastExtracted((cur) => ({ ...(cur || {}), language: code }));
                    send(text, 'language');
                  }}
                />
              )}

              {sitePrefillHint && (
                <div className="ob-prefill-hint" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px', padding: '8px 12px', borderRadius: 10, background: 'var(--accent-soft, rgba(122,91,255,0.09))', border: '1px solid var(--accent-soft-2, rgba(122,91,255,0.2))', font: '500 12.5px/1.45 Inter, sans-serif', color: 'var(--text-2, inherit)' }}>
                  <span aria-hidden="true">🌐</span>
                  <span>{t('onboardingChat.sitePrefillHint', { defaultValue: "Your site's already here — hit Send and we'll turn it into your brand. Or drop in a different link first." })}</span>
                </div>
              )}
              <div className="ob-textbox">
                <textarea ref={inputRef}
                          rows={2}
                          value={input}
                          onChange={(e) => { setInput(e.target.value); if (sitePrefillHint) setSitePrefillHint(false); }}
                          onKeyDown={onKeyDown}
                          placeholder={showVisualStylePicker
                            ? t('onboardingChat.placeholderVisualStyle')
                            : showLanguagePicker
                              ? t('onboardingChat.placeholderLanguage')
                              : t('onboardingChat.placeholderTypeAnswer')}
                          disabled={sending} />
                <div className="ob-textbox-foot">
                  {/* Attach + Voice tool icons removed — they were non-functional
                      placeholders (no handlers). Send stays right-aligned. */}
                  <button type="submit" className="btn btn-primary btn-sm ob-send-btn"
                          disabled={!input.trim() || sending}>
                    {sending ? (
                      <>
                        <SendSpinner /> {t('onboardingChat.thinking')}
                      </>
                    ) : (
                      <>
                        {t('onboardingChat.send')} <I.send size={12} />
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Picker, when shown, replaces chips. Otherwise AI chips
                  sit below the textbox like usual. The language picker
                  also suppresses chips — the AI tends to suggest language
                  names there, which duplicated the picker. */}
              {!showVisualStylePicker && !showLanguagePicker && chips.length > 0 && (
                <div className="ob-sugg">
                  <span className="ob-sugg-l">{chips?.isDefault ? t('onboardingChat.examples') : t('onboardingChat.quickReplies')}</span>
                  {chips.map((s, i) => (
                    <button key={i} type="button" className="ob-sugg-pill"
                            disabled={sending}
                            onClick={() => send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </form>
          )}
        </div>
      </div>
      <QuickBrandSheet
        open={quickBrandOpen}
        source={quickMode ? 'posting_redirect' : 'onboarding'}
        onClose={() => {
          if (quickMode) leaveOnboarding();
          else setQuickBrandOpen(false);
        }}
        onCreated={() => onComplete?.('dashboard', { onboardingCompleted: true })}
      />
    </div>
  );
}

export default Onboarding;

/* ---------------- helpers ---------------- */

// Split a long AI message into bubbles for the drip-in effect.
// Splits on blank lines (paragraph breaks). Does NOT split on
// sentence boundaries because that breaks on common abbreviations
// like "e.g.", "i.e.", "etc." mid-question. AI rarely produces
// 400+ char messages anyway, and a single longer bubble reads
// fine; better than a question torn in half at a period.
function splitParagraphs(text) {
  const paras = text.split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean);
  if (paras.length > 1) return paras.slice(0, 4);
  return [text];
}

function humanStep(step, t) {
  // Make backend step keys feel like a human label in the topbar
  if (!step) return t('onboardingChat.stepFallback');
  return step
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ---------------- website auto-analysis helpers ---------------- */

// Hosts we never try to fetch: social/app links can't be read without JS
// or auth, so analyzing them only produces an ugly silent failure. For
// these the chat behaves exactly as before (the AI just captures the URL).
const NON_ANALYZABLE_HOSTS = /(^|\.)(instagram\.com|facebook\.com|fb\.com|m\.me|t\.me|telegram\.me|telegram\.org|linkedin\.com|x\.com|twitter\.com|tiktok\.com|youtube\.com|youtu\.be|threads\.net|wa\.me|whatsapp\.com|pinterest\.[a-z.]+|goo\.gl|maps\.app\.goo\.gl|dathent\.ai)$/i;

// Find the first fetchable website link in a chat message. Accepts both
// explicit http(s) URLs and bare domains ("mybrand.com") — lazy typing is
// the whole point of this feature. Bare domains are restricted to common
// TLDs (compound ones first) so prose like "e.g." or emails never match;
// the `[^@\w.]` guard keeps "name@gmail.com" from matching "gmail.com".
//
// The list used to cover the EU + Ukraine only, so a user who typed the
// SHORTEST form of their address — "brashop.ru", no scheme, no www — got no
// site scan at all and the interview kept asking what the site already said
// ("а по сайту понять никак?", real signup 2026-08-09, left after 33s). It now
// carries the rest of Europe, the Caucasus/Central Asia and the major
// non-European markets. `ru` and `by` are deliberately absent — owner decision
// 2026-08-09: those territories are out of scope commercially. Note this is an
// onboarding CONVENIENCE list, not a gate: an explicit https:// or www. link
// is still fetched whatever its TLD, so it must not be mistaken for a block.
function extractWebsiteUrlFromText(text) {
  const value = String(text || '');
  let raw = value.match(/https?:\/\/[^\s<>"')]+/i)?.[0] || null;
  if (!raw) {
    const bare = value.match(/(?:^|[^@\w.])((?:www\.[a-z0-9][\w-]*(?:\.[a-z0-9-]+)+|[a-z0-9][\w-]{0,62}\.(?:com\.ua|co\.uk|com\.br|com\.au|co\.nz|com\.tr|com\.mx|com\.ar|com\.sg|com\.hk|com\.cn|co\.il|co\.za|co\.jp|co\.kr|co\.in|com|net|org|io|co|ai|app|dev|shop|store|biz|info|online|site|studio|agency|design|me|eu|us|uk|ua|de|fr|es|it|nl|pl|cz|sk|lt|lv|ee|se|no|dk|fi|pt|at|ch|be|ie|hu|ro|bg|hr|si|rs|gr|kz|uz|md|ge|am|az|kg|tr|il|ae|in|br|au|nz|ca|mx|ar|cl|za|sg|hk|jp|cn|kr))(?:\/[^\s<>"')]*)?)/i);
    raw = bare ? bare[1] : null;
  }
  if (!raw) return null;
  raw = raw.replace(/[).,;:!?…]+$/, '');
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  let url;
  try { url = new URL(candidate); } catch { return null; }
  const host = url.hostname.toLowerCase();
  if (!host.includes('.') || NON_ANALYZABLE_HOSTS.test(host)) return null;
  return { href: url.href, host };
}

// Keep only fields the backend website-scan patch shape defines, trimmed and
// non-empty (matches the onboarding worker's normalizeSuggestedPatch output).
// Split a "·"/comma/newline-joined display string back into a clean array
// (the intake card edits array fields like pillars/goals as one text line).
function splitList(value) {
  return String(value || '')
    .split(/[·,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 10);
}

// Join an array field into a "·"-separated display string for the card.
function joinList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v || '').trim()).filter(Boolean).join(' · ');
  return typeof value === 'string' ? value : '';
}

function sitePreviewRows(patch, t) {
  if (!patch) return [];
  const rows = [];
  const pushText = (key, labelKey, value) => {
    if (typeof value === 'string' && value.trim()) {
      const trimmed = value.trim();
      rows.push({ key, label: t(labelKey), value: trimmed.length > 140 ? `${trimmed.slice(0, 139).trimEnd()}…` : trimmed });
    }
  };
  pushText('about', 'onboardingChat.siteFieldAbout', patch.businessDescription);
  pushText('product', 'onboardingChat.siteFieldProduct', patch.productOrService);
  pushText('audience', 'onboardingChat.siteFieldAudience', patch.targetAudience);
  pushText('offer', 'onboardingChat.siteFieldOffer', patch.offer);
  // Topics render as pills — keep them in the visible window ahead of tone,
  // they're the most scannable proof the analyzer understood the site.
  if (Array.isArray(patch.contentPillars) && patch.contentPillars.length) {
    rows.push({
      key: 'topics',
      label: t('onboardingChat.siteFieldTopics'),
      items: patch.contentPillars.slice(0, 5).map((item) => String(item)),
    });
  }
  pushText('tone', 'onboardingChat.siteFieldTone', patch.toneOfVoice);
  return rows.slice(0, 5);
}
